// src/documents/xlsx/xlsxZipUpdater.js
//
// Surgical xlsx cell-value updater. JSZip ile template'in xl/worksheets/sheet1.xml
// dosyasını okuyup yalnızca belirtilen hücrelerin <v> değerini değiştirir, geri
// kalan her şeyi (styles.xml, sharedStrings vs.) aynen korur. SheetJS'in
// Community Edition write'ında border/style kaybı sorununu by-pass eder.
//
// Sınırlama: sadece template'de zaten var olan hücreleri güncelleyebilir
// (sparse hücre eklemez). Şablonlarımızda tüm ilgili hücreler placeholder
// değerlerle mevcut olduğu için yeterli.

import JSZip from 'jszip';

// Excel column letter → 0-based index (A=0, Z=25, AA=26, …)
function colToIdx(col) {
  let n = 0;
  for (const ch of col) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}
function idxToCol(idx) {
  let n = idx + 1;
  let s = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

/** "A237:DH237" gibi range'i hücre adres listesine açar. Tek hücre ("A237")
 *  döndürürse [A237]. */
function expandCellRange(range) {
  if (!range) return [];
  if (!range.includes(':')) return [range];
  const [a, b] = range.split(':');
  const ma = /^([A-Z]+)(\d+)$/.exec(a);
  const mb = /^([A-Z]+)(\d+)$/.exec(b);
  if (!ma || !mb) return [range];
  const c1 = colToIdx(ma[1]); const r1 = parseInt(ma[2], 10);
  const c2 = colToIdx(mb[1]); const r2 = parseInt(mb[2], 10);
  const out = [];
  for (let r = r1; r <= r2; r++) {
    for (let c = c1; c <= c2; c++) {
      out.push(`${idxToCol(c)}${r}`);
    }
  }
  return out;
}

function escapeXml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildCellTag(addr, attrs, value) {
  // Mevcut t="..." attr'ını çıkar, yenisini ekleyeceğiz
  const cleanAttrs = String(attrs || '').replace(/\s*t="[^"]*"/g, '');
  if (typeof value === 'string') {
    // Inline string — sharedStrings tablosunu mutate etmemek için
    return `<c r="${addr}"${cleanAttrs} t="inlineStr"><is><t>${escapeXml(value)}</t></is></c>`;
  }
  // Numerik veya boolean
  return `<c r="${addr}"${cleanAttrs} t="n"><v>${value}</v></c>`;
}

function updateCellValue(xml, addr, value) {
  // ÖNEMLİ: self-closing form'u ÖNCE dene. Çünkü full-form regex'i greedy
  // olmasa da `<c r="X" s="N"/>` gibi self-closing'i kabul edip ardından
  // başka hücrelerin </c>'sine kadar uzanır — aradaki tüm hücreleri yok eder.
  const reSelf = new RegExp(`<c r="${addr}"([^/>]*?)/>`, 'g');
  let replaced = false;
  xml = xml.replace(reSelf, (full, attrs) => {
    replaced = true;
    return buildCellTag(addr, attrs, value);
  });
  if (replaced) return xml;

  // Full form: <c r="ADDR" attrs>content</c>. Self-closing değilse buradayız.
  const reFull = new RegExp(`<c r="${addr}"([^>]*?)>[\\s\\S]*?</c>`, 'g');
  xml = xml.replace(reFull, (full, attrs) => {
    replaced = true;
    return buildCellTag(addr, attrs, value);
  });
  if (!replaced) {
    console.warn(`[xlsxZipUpdater] Cell ${addr} not found in sheet XML — update atlandı.`);
  }
  return xml;
}

/** <row r="N" ...> attribute'larına hidden="1" ht="0" customHeight="1" ekle. */
function hideRowInXml(xml, oneBasedRow) {
  const reFull = new RegExp(`<row r="${oneBasedRow}"([^>]*)>`, 'g');
  let replaced = false;
  xml = xml.replace(reFull, (full, attrs) => {
    if (full.endsWith('/>')) return full; // self-closing — alttaki ile yakalanır
    replaced = true;
    const clean = String(attrs || '')
      .replace(/\s*hidden="[^"]*"/g, '')
      .replace(/\s*ht="[^"]*"/g, '')
      .replace(/\s*customHeight="[^"]*"/g, '');
    return `<row r="${oneBasedRow}"${clean} hidden="1" ht="0" customHeight="1">`;
  });
  if (replaced) return xml;

  // Self-closing form
  const reSelf = new RegExp(`<row r="${oneBasedRow}"([^/>]*?)/>`, 'g');
  xml = xml.replace(reSelf, (full, attrs) => {
    replaced = true;
    const clean = String(attrs || '')
      .replace(/\s*hidden="[^"]*"/g, '')
      .replace(/\s*ht="[^"]*"/g, '')
      .replace(/\s*customHeight="[^"]*"/g, '');
    return `<row r="${oneBasedRow}"${clean} hidden="1" ht="0" customHeight="1"/>`;
  });
  if (replaced) return xml;

  // Şablonda <row> entry'si yoksa, sheetData içinde sıralı pozisyona ekle
  // (basit yaklaşım: doğrudan </sheetData>'dan önce ekle — Excel re-sortlar).
  return xml.replace(/<\/sheetData>/,
    `<row r="${oneBasedRow}" hidden="1" ht="0" customHeight="1"/></sheetData>`);
}

/**
 * Şablondaki <mergeCells> listesine yeni merge'leri ekle.
 * Mevcutta var olan ref'leri tekrar eklemez (idempotent).
 * Element hiç yoksa </sheetData>'dan hemen sonra oluşturur.
 */
function addMergesToXml(xml, merges) {
  if (!merges?.length) return xml;
  // Var olan tüm merge ref'leri çıkar (duplikasyon önlemek için)
  const existing = new Set();
  const mergeRe = /<mergeCell\s+ref="([^"]+)"\s*\/>/g;
  let m;
  while ((m = mergeRe.exec(xml)) !== null) existing.add(m[1]);

  const toAdd = merges.filter(r => !existing.has(r));
  if (toAdd.length === 0) return xml;

  // <mergeCells count="N"> elementi var mı?
  const blockRe = /<mergeCells\s+count="(\d+)"\s*>([\s\S]*?)<\/mergeCells>/;
  const match = blockRe.exec(xml);
  const inject = toAdd.map(r => `<mergeCell ref="${r}"/>`).join('');
  if (match) {
    const oldCount = parseInt(match[1], 10) || 0;
    const newCount = oldCount + toAdd.length;
    return xml.replace(blockRe,
      `<mergeCells count="${newCount}">${match[2]}${inject}</mergeCells>`);
  }
  // Element yok — sheetData'dan hemen sonra ekle
  return xml.replace(/<\/sheetData>/,
    `</sheetData><mergeCells count="${toAdd.length}">${inject}</mergeCells>`);
}

/**
 * Hedef hücrelerin mevcut stilini koruyup belirli kenarlık(lar) ekle.
 *
 * Çalışma şekli:
 *   1. styles.xml içindeki <cellXfs> listesinden hedef hücrenin xf'ini bul.
 *   2. xf'in mevcut borderId'sini al, bunu kopyalayıp istenen border'ı eklenmiş
 *      yeni bir <border> üret (henüz yoksa <borders>'a ekle).
 *   3. Yeni borderId ile yeni bir <xf> üret (mevcut xf'in font/fill/alignment'ını
 *      kopyalar, sadece borderId değişir + applyBorder="1").
 *   4. Hedef hücrenin s="..." attribute'unu yeni xf'in index'ine ayarla.
 *
 * @param {Object} zip - JSZip instance
 * @param {string} sheetXml - mevcut sheet1.xml
 * @param {Array<{cells: string[], borders: {top?,bottom?,left?,right?: 'thick'|'medium'|'thin'}}>} ops
 * @returns {Promise<{sheetXml: string}>}
 */
async function applyCellBorderOverrides(zip, sheetXml, ops) {
  if (!ops?.length) return { sheetXml };
  const stylesEntry = zip.file('xl/styles.xml');
  if (!stylesEntry) return { sheetXml };
  let stylesXml = await stylesEntry.async('string');

  // <borders count="N"> ... </borders> bloğunu yakala
  const bordersBlockRe = /<borders\s+count="(\d+)"\s*>([\s\S]*?)<\/borders>/;
  const bordersMatch = bordersBlockRe.exec(stylesXml);
  if (!bordersMatch) return { sheetXml };
  const allBordersInner = bordersMatch[2];
  // Parse her <border>...</border>'ı diziye al
  const borderRe = /<border[^>]*>(?:[\s\S]*?<\/border>|\s*\/>)/g;
  const borderList = [];
  let bm;
  while ((bm = borderRe.exec(allBordersInner)) !== null) borderList.push(bm[0]);

  // <cellXfs count="N">...</cellXfs> bloğunu yakala
  const xfsBlockRe = /<cellXfs\s+count="(\d+)"\s*>([\s\S]*?)<\/cellXfs>/;
  const xfsMatch = xfsBlockRe.exec(stylesXml);
  if (!xfsMatch) return { sheetXml };
  const xfRe = /<xf\b[^/]*\/>|<xf\b[^>]*>[\s\S]*?<\/xf>/g;
  const xfList = [];
  let xm;
  while ((xm = xfRe.exec(xfsMatch[2])) !== null) xfList.push(xm[0]);

  // Cache: borderId-eklenecek-spec → new borderId
  const borderCache = new Map();
  // Cache: (oldStyleIdx, borderId) → new style idx
  const xfCache = new Map();

  function getOrCreateBorder(oldBorderId, spec) {
    const key = `${oldBorderId}|${JSON.stringify(spec)}`;
    if (borderCache.has(key)) return borderCache.get(key);
    const oldBorder = borderList[oldBorderId] || '<border/>';
    // Mevcut <border> elementinden top/bottom/left/right'ı çıkar, spec ile override et
    function replaceSide(side) {
      const sideRe = new RegExp(`<${side}[^/]*(/>|>[\\s\\S]*?</${side}>)`);
      const newStyle = spec[side];
      if (newStyle) {
        const tag = `<${side} style="${newStyle}"><color auto="1"/></${side}>`;
        if (sideRe.test(oldBorder)) {
          // dummy below
        }
        // strip old + insert new yapıldı aşağıda; placeholder
        return tag;
      }
      const m = sideRe.exec(oldBorder);
      return m ? m[0] : `<${side}/>`;
    }
    const newBorder = `<border>${replaceSide('left')}${replaceSide('right')}${replaceSide('top')}${replaceSide('bottom')}<diagonal/></border>`;
    const newId = borderList.length;
    borderList.push(newBorder);
    borderCache.set(key, newId);
    return newId;
  }

  function getOrCreateXf(oldIdx, newBorderId) {
    const key = `${oldIdx}|${newBorderId}`;
    if (xfCache.has(key)) return xfCache.get(key);
    const oldXf = xfList[oldIdx] || '<xf/>';
    // borderId attr'ını değiştir, applyBorder="1" ekle
    let cleaned = oldXf.replace(/\s*borderId="[^"]*"/, '').replace(/\s*applyBorder="[^"]*"/, '');
    cleaned = cleaned.replace(/<xf\b/, `<xf borderId="${newBorderId}" applyBorder="1"`);
    const newIdx = xfList.length;
    xfList.push(cleaned);
    xfCache.set(key, newIdx);
    return newIdx;
  }

  // Her operasyon için hedef hücreleri işle
  for (const op of ops) {
    if (!op.cells?.length || !op.borders) continue;
    // Range desteği: "A237:DH237" → tek tek hücreler
    const expanded = op.cells.flatMap(c => expandCellRange(c));
    for (const cellAddr of expanded) {
      // Hücrenin mevcut s="N" değerini bul
      const cellRe = new RegExp(`<c r="${cellAddr}"\\s+s="(\\d+)"`);
      const cm = cellRe.exec(sheetXml);
      if (!cm) continue;
      const oldStyleIdx = parseInt(cm[1], 10);
      const oldXf = xfList[oldStyleIdx] || '';
      const oldBorderIdMatch = /borderId="(\d+)"/.exec(oldXf);
      const oldBorderId = oldBorderIdMatch ? parseInt(oldBorderIdMatch[1], 10) : 0;
      const newBorderId = getOrCreateBorder(oldBorderId, op.borders);
      const newStyleIdx = getOrCreateXf(oldStyleIdx, newBorderId);
      // Hücrenin s="..." değerini güncelle
      sheetXml = sheetXml.replace(cellRe, `<c r="${cellAddr}" s="${newStyleIdx}"`);
    }
  }

  // styles.xml'i yeniden inşa et
  const newBordersBlock = `<borders count="${borderList.length}">${borderList.join('')}</borders>`;
  const newXfsBlock = `<cellXfs count="${xfList.length}">${xfList.join('')}</cellXfs>`;
  stylesXml = stylesXml.replace(bordersBlockRe, newBordersBlock);
  stylesXml = stylesXml.replace(xfsBlockRe, newXfsBlock);
  zip.file('xl/styles.xml', stylesXml);

  return { sheetXml };
}

/** <dimension ref="..."/> elementindeki ref'i güncelle. */
function setDimensionInXml(xml, ref) {
  const re = /<dimension\s+ref="[^"]*"\s*\/>/;
  if (re.test(xml)) return xml.replace(re, `<dimension ref="${ref}"/>`);
  // Yoksa <worksheet ...> hemen sonrasına ekle
  return xml.replace(/(<worksheet[^>]*>)/, `$1<dimension ref="${ref}"/>`);
}

/**
 * @param {ArrayBuffer|Uint8Array} templateBytes
 * @param {Object<string, string|number|boolean>} updates - {cellAddr: newValue}
 * @returns {Promise<ArrayBuffer>} updated xlsx bytes
 */
export async function applyCellUpdatesToXlsx(templateBytes, updates) {
  return applyXlsxUpdates(templateBytes, { cells: updates });
}

/**
 * Genel xlsx update — cell values + hidden rows + dimension ref desteği.
 * @param {ArrayBuffer|Uint8Array} templateBytes
 * @param {Object} opts
 * @param {Object<string, *>} [opts.cells]         - cell value updates
 * @param {Array<number>}      [opts.hiddenRows]   - 1-bazlı satır numaraları (gizle)
 * @param {string}             [opts.dimensionRef] - "A1:DJ51" gibi
 * @param {Array<string>}      [opts.mergeCells]   - ["T204:Y207", ...] eksik merge'leri enjekte et
 * @param {Array<{cells:string[],borders:Object}>} [opts.cellBorders] - hedef hücrelere
 *        kenarlık ekle. borders: { top|bottom|left|right: 'thick'|'medium'|'thin' }
 * @returns {Promise<ArrayBuffer>}
 */
export async function applyXlsxUpdates(templateBytes, opts = {}) {
  const zip = await JSZip.loadAsync(templateBytes);
  const sheetFiles = zip.file(/xl\/worksheets\/sheet\d+\.xml/);
  if (!sheetFiles || sheetFiles.length === 0) {
    throw new Error('xlsxZipUpdater: worksheet xml bulunamadı');
  }
  const sheetFile = sheetFiles[0];
  let sheetXml = await sheetFile.async('string');

  if (opts.cells) {
    for (const [addr, value] of Object.entries(opts.cells)) {
      if (value === undefined || value === null) continue;
      sheetXml = updateCellValue(sheetXml, addr, value);
    }
  }
  if (Array.isArray(opts.hiddenRows)) {
    for (const r of opts.hiddenRows) sheetXml = hideRowInXml(sheetXml, r);
  }
  if (Array.isArray(opts.mergeCells)) {
    sheetXml = addMergesToXml(sheetXml, opts.mergeCells);
  }
  if (Array.isArray(opts.cellBorders) && opts.cellBorders.length > 0) {
    const res = await applyCellBorderOverrides(zip, sheetXml, opts.cellBorders);
    sheetXml = res.sheetXml;
  }
  if (opts.dimensionRef) {
    sheetXml = setDimensionInXml(sheetXml, opts.dimensionRef);
  }

  zip.file(sheetFile.name, sheetXml);
  return await zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' });
}
